"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';
import HeaderPage from "@/components/header-page";
import MuseumCard from "@/components/musse-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, X, ChevronDown, ChevronUp, Building, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

interface MuseumApiResponse {
  musees: {
    current_page: number;
    data: any[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
}

interface FilterOptions {
  destinations: any[];
}

export default function MuseumsClient() {
  const t = useTranslations();
  const locale = useLocale();
  const { getLocalizedField } = useLocalizedField();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // State for museums data
  const [museums, setMuseums] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<MuseumApiResponse['musees'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    destinations: [],
  });

  // Build query parameters for API
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") {
      params.append("destination_id", selectedDestination);
    }
    
    return params;
  };

  // Build URL parameters for UI
  const buildUrlParams = () => {
    const params = new URLSearchParams();
    
    if (currentPage !== 1) params.append("page", currentPage.toString());
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") params.append("destination", selectedDestination);
    
    return params;
  };

  // Update URL when filters change
  const updateUrl = () => {
    if (isInitialLoad) return;
    
    const params = buildUrlParams();
    const queryString = params.toString();
    const newUrl = queryString ? `/museums?${queryString}` : '/museums';
    
    router.push(newUrl, { scroll: false });
  };

  // Fetch filter options (only destinations)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch destinations only
        const destinationsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}alldestinations`);
        const destinationsData = await destinationsResponse.json();
        
        const destinationsList: any[] = destinationsData.destinations || [];
        const sortedDestinations = destinationsList.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        setFilterOptions({
          destinations: sortedDestinations,
        });
        setLoadingFilters(false);
      } catch (error) {
        console.error("Error fetching destinations:", error);
        setLoadingFilters(false);
      }
    };
    
    fetchFilterOptions();
  }, []);

  // Initialize state from URL params
  useEffect(() => {
    if (!searchParams || !isInitialLoad) return;
    
    const pageParam = searchParams.get("page");
    const searchParam = searchParams.get("search");
    const destinationParam = searchParams.get("destination");
    
    if (pageParam) {
      const page = parseInt(pageParam);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
      }
    }
    
    if (searchParam) setSearchQuery(searchParam);
    if (destinationParam) setSelectedDestination(destinationParam);
    
    setIsInitialLoad(false);
  }, [searchParams, isInitialLoad]);

  // Update URL when filters change
  useEffect(() => {
    updateUrl();
  }, [currentPage, searchQuery, selectedDestination]);

  // Fetch museums based on filters
  useEffect(() => {
    const fetchMuseums = async () => {
      if (isInitialLoad || loadingFilters) return;
      
      setLoading(true);
      try {
        const apiParams = buildQueryParams();
        
        // Fetch data from museums API endpoint
        const apiUrl = `${process.env.NEXT_PUBLIC_API_URL}musees?${apiParams.toString()}`;
        const response = await fetch(apiUrl);
        const data: MuseumApiResponse = await response.json();
        
        setMuseums(data.musees.data);
        setPaginationData(data.musees);
      } catch (error) {
        console.error("Error fetching museums:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMuseums();
  }, [currentPage, searchQuery, selectedDestination, isInitialLoad, loadingFilters]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle filter changes
  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle pagination
  const handlePageChange = (url: string | null) => {
    if (!url) return;
    
    try {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get("page");
      if (pageParam) {
        const page = parseInt(pageParam);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error parsing page URL:", error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
    
    router.push('/museums', { scroll: false });
  };

  // Clear single filters
  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearDestination = () => {
    setSelectedDestination("all");
    setCurrentPage(1);
  };

  // Get selected destination name for display
  const getSelectedDestinationName = () => {
    if (selectedDestination === "all") return null;
    const destination = filterOptions.destinations.find(d => d.slug === selectedDestination);
    return destination ? destination.name : selectedDestination;
  };

  // Function to render pagination links
  const renderPaginationLinks = () => {
    if (!paginationData || !paginationData.links) return null;

    return paginationData.links.map((link, index) => {
      if (link.label === "...") {
        return (
          <PaginationItem key={index}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const isPrevious = link.label.includes("previous");
      const isNext = link.label.includes("next");
      
      if (isPrevious) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.prev_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.prev_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (isNext) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.next_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.next_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                </>
              ) : (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      const pageNum = parseInt(link.label);
      if (!isNaN(pageNum)) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(link.url);
              }}
              isActive={link.active}
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return null;
    });
  };

  // Filter sidebar component
  const FilterSidebar = () => (
    <div className="bg-card rounded-xl p-2 shadow-sm border border-border space-y-2">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Building className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('museums.filters')}</h2>
      </div>

      {/* Search Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('museums.search')} :</h3>
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Input
              placeholder={t('museums.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-3"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Destination Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('museums.destination')} :</h3>
        <Select
          value={selectedDestination}
          onValueChange={handleDestinationChange}
          disabled={loadingFilters}
        >
          <SelectTrigger className="w-full flex items-center">
            <SelectValue placeholder={t('museums.allDestinations')}>
              {selectedDestination !== "all"
                ? getSelectedDestinationName()
                : t('museums.allDestinations')}
            </SelectValue>
            {selectedDestination !== "all" && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearDestination();
                }}
                className="ml-2"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </SelectTrigger>
          <SelectContent className="max-h-[400px] overflow-y-auto">
            <SelectGroup>
              <SelectItem value="all" className="font-semibold py-3">
                {t('museums.allDestinations')}
              </SelectItem>
              {filterOptions.destinations.map((destination) => (
                <SelectItem
                  key={destination.id}
                  value={destination.slug}
                  className="py-2 text-sm text-gray-600"
                >
                  {destination.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Active Filters */}
      {(selectedDestination !== "all" || searchQuery) && (
        <div className="pt-4 border-t space-y-4">
          <h3 className="text-lg font-semibold">{t('museums.activeFilters')}</h3>
          <div className="space-y-2">
            {searchQuery && (
              <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-sm text-blue-700">
                  {t('museums.search')}: "{searchQuery}"
                </span>
                <button
                  onClick={clearSearch}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {selectedDestination !== "all" && (
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md">
                <span className="text-sm text-green-700">
                  {t('museums.destination')}: {getSelectedDestinationName()}
                </span>
                <button
                  onClick={clearDestination}
                  className="text-green-500 hover:text-green-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="w-full"
          >
            {t('museums.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('navigation.museums')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.museums'), href: "/museums" },
        ]}
      />
      
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Button and Active Filters */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('museums.filters')}
              {showMobileFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            
            {/* Active filters chips for mobile */}
            <div className="flex gap-2 overflow-x-auto">
              {selectedDestination !== "all" && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  {getSelectedDestinationName()}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={clearDestination} />
                </span>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          {/* <Card className="mb-4">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('museums.searchPlaceholderMobile')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-gray-300"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-3 top-3"
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
                <Button type="submit">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card> */}

          {/* Mobile Filters Dropdown */}
          {showMobileFilters && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <FilterSidebar />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters (Desktop) */}
          <div className="hidden lg:block lg:w-1/4 border-r-2 border-gray-200 pr-8">
            <div className="sticky top-24">
              <FilterSidebar />
            </div>
          </div>

          {/* Right Content - Museums List */}
          <div className="lg:w-3/4">
            {/* Desktop Search */}
            <div className="hidden lg:block mb-6">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('museums.searchPlaceholderDesktop')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </form>
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                {loading ? t('museums.loadingMuseums') : `${t('museums.found')} ${paginationData?.total || 0} ${t('museums.museumsCount')}`}
              </h2>
              {paginationData && !loading && (
                <p className="text-gray-600">
                  {t('museums.showing')} {paginationData.from} {t('museums.to')} {paginationData.to} {t('museums.of')} {paginationData.total} {t('museums.results')}
                  {paginationData.current_page && ` (${t('museums.page')} ${paginationData.current_page} ${t('museums.of')} ${paginationData.last_page})`}
                </p>
              )}
            </div>
            
            {/* Museums Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <Skeleton className="h-48 w-full" />
                    <div className="p-4">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3 mb-4" />
                      <div className="flex justify-between">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : museums.length === 0 ? (
              <div className="flex flex-col gap-1 justify-center items-center">
                <div className="text-gray-400 mb-4">
                  <Building className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('museums.noMuseumsFound')}</h3>
                <p className="text-gray-500 mb-6">{t('museums.tryAdjustingFilters')}</p>
                <Button onClick={clearFilters}>{t('museums.clearAllFilters')}</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                  {museums.map((museum) => (
                    <MuseumCard
                      key={museum.id}
                      item={museum}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {paginationData && paginationData.last_page > 1 && (
                  <div className="mt-8" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    <Pagination>
                      <PaginationContent className={locale === 'ar' ? 'flex-row-reverse' : ''}>
                        {renderPaginationLinks()}
                      </PaginationContent>
                    </Pagination>
                    
                    <div className="mt-4 text-center text-sm text-gray-600">
                      <p>
                        {t('museums.page')} {paginationData.current_page} {t('museums.of')} {paginationData.last_page}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}